import { get, useFormContext } from "react-hook-form";
import { DropdownDynamicList } from "../../../../components/dropdowns/DropDownDynamicList";
import { ErrorText } from "../../../../components/forms/ErrorText";
import { FORM_MSG, REGEX_TEST } from "../../../../config";
import { CreateProgramPopup } from "../../../../types/ClientProgram";
import { InputRFH } from "../../../../components/inputs/InputRFH";
import { formHandleEmpty, formHandleEmptyNumbers } from "../../../../helpers/forms";
import { NumberFormatRFH } from "../../../../components/inputs/NumberFormatRFH";
import { NumberFormatInput } from "../../../../components/inputs/NumberFormatInput";
import { CheckboxRFH } from "../../../../components/inputs/CheckboxRFH";
import { DropDownGeneric } from "../../../../components/dropdowns";
import Input from "../../../../components/inputs/Input";
import { AccountNumbers } from "../../../../components/inputs/AccountNumbers";
import { SelectCreditReport } from "../../../../components/inputs/SelectCreditReport";
import { DropDownCreditors } from "../../../../components/dropdowns/DropDownCreditors";
import { PaymentsDynamicFields } from "../../../../views/Popups/Programs/ProgramPaymentFields";

export const CreateProgramField = ({
    f,
    isEditable,
    width,
    fieldValidate,
    showInfo,
    priceError,
    validating,
    person_id,
    lead,
    isEditingProgram,
}: {
    f: any,
    isEditable: boolean,
    width: string,
    fieldValidate: any,
    showInfo?: boolean,
    priceError?: string,
    validating?: boolean,
    person_id: string,
    lead?: boolean,
    isEditingProgram?: boolean,
}) => {
    const {
        register,
        watch,
        setError,
        clearErrors,
        setValue,
        control,
        formState: { errors },
    } = useFormContext<CreateProgramPopup>();

    const options = watch();


    /**
     * For deeply nested object properties, errors have to access
     * via the get method.
     * @param key Property key
     * @returns error object
     */
    const getErrorType = (key: string) => get(errors, key)?.type;


    switch (f.type) {
        case "DynamicDropdown":
            return (
                <DropdownDynamicList
                    key={f.title}
                    title={f.title}
                    editing={isEditable}
                    readonly={!isEditable}
                    name={f.accesKey}
                    className="flex-col"
                    outerClass={width}
                    menuPosition={"fixed"}
                    titleClassName="text-r-medium-small-2"
                    row={false}
                    onChangeFunction={(val) =>
                        setValue(f.accesKey as any, val)
                    }
                    placeholder={f.title}
                    search={f.search}
                    values={(options as any)[f.accesKey]}
                >
                    {getErrorType(f.accesKey) === "required" && (
                        <ErrorText msg={FORM_MSG.REQUIRED} />
                    )}
                </DropdownDynamicList>
            );
        case "Accounts":
            return (
                <div
                    key={f.title}
                    className={`flex flex-col w-full ${width}`}
                >
                    <div className="flex flex-row w-full">
                        <div className="flex flex-col items-start w-1/2 ">
                            <span className="ml-1 mr-4 sm:mr-0">Accounts + </span>
                            <InputRFH
                                className="flex-col w-full pr-2"
                                name="accounts_positive"
                                containerClassName="!mb-0"
                                editing={isEditable}
                                inputRegister={register}
                                validation={{
                                    pattern: REGEX_TEST.NUMBER,
                                    ...formHandleEmptyNumbers,
                                    required: f.required,
                                }}
                            >
                                <>
                                    {getErrorType("accounts_positive") ===
                                        "pattern" && (
                                            <ErrorText
                                                msg={FORM_MSG.INVALID_NUMBER}
                                            />
                                        )}
                                    {getErrorType("accounts_positive") ===
                                        "required" && (
                                            <ErrorText
                                                msg={FORM_MSG.REQUIRED}
                                            />
                                        )}
                                </>
                            </InputRFH>
                        </div>
                        <div className="flex flex-col items-start w-1/2">
                            <span className="ml-1 mr-4 sm:mr-0 pl-2">
                                Accounts -
                            </span>
                            <InputRFH
                                className="flex-col w-full pl-2"
                                containerClassName="!mb-0"
                                name="accounts_negative"
                                editing={isEditable}
                                inputRegister={register}
                                validation={{
                                    pattern: REGEX_TEST.NUMBER,
                                    ...formHandleEmptyNumbers,
                                    required: f.required,
                                }}
                            >
                                <>
                                    {getErrorType("accounts_negative") ===
                                        "pattern" && (
                                            <ErrorText
                                                msg={FORM_MSG.INVALID_NUMBER}
                                            />
                                        )}
                                    {getErrorType("accounts_negative") ===
                                        "required" && (
                                            <ErrorText
                                                msg={FORM_MSG.REQUIRED}
                                            />
                                        )}
                                </>
                            </InputRFH>
                        </div>
                    </div>
                    {getErrorType("accounts_positive") === "validate" && (
                        <ErrorText
                            msg={errors.accounts_positive?.message || ""}
                        />
                    )}
                    {getErrorType("accounts_negative") === "validate" && (
                        <ErrorText
                            msg={errors.accounts_negative?.message || ""}
                        />
                    )}
                </div>
            );
        case "Number":
            return (
                <div
                    key={f.title}
                    className={`${width} flex sm:flex-col items-center sm:items-start`}
                >
                    <span className="text-r-medium-small-2">
                        {f.title}
                    </span>
                    <InputRFH
                        editing={isEditable}
                        className="flex-col w-full"
                        containerClassName="!mb-0"
                        name={f.accesKey}
                        type={f.typeInput || undefined}
                        inputRegister={register}
                        validation={{
                            pattern: REGEX_TEST.NUMBER,
                            ...formHandleEmptyNumbers,
                            required: f.required,
                            validate: fieldValidate?.validate,
                        }}
                    >
                        <>
                            {getErrorType(f.accesKey) === "pattern" && (
                                <ErrorText msg={FORM_MSG.INVALID_NUMBER} />
                            )}
                            {getErrorType(f.accesKey) === "required" && (
                                <ErrorText msg={FORM_MSG.REQUIRED} />
                            )}
                            {fieldValidate &&
                                getErrorType(f.accesKey) === "validate" && (
                                    <ErrorText
                                        msg={fieldValidate.message}
                                    />
                                )}
                        </>
                    </InputRFH>
                </div>
            );
        case "Pay Day":
            return (
                <div
                    key={f.title}
                    className={`${width} flex sm:flex-col items-center sm:items-start`}
                >
                    <span className="text-r-medium-small-2">
                        {f.title}
                    </span>
                    <InputRFH
                        editing={isEditable}
                        className="flex-col w-full"
                        inputClassName="!mb-0"
                        name={f.accesKey}
                        type={f.typeInput || undefined}
                        inputRegister={register}
                        validation={{
                            pattern: REGEX_TEST.NUMBER,
                            ...formHandleEmpty,
                            required: f.required,
                            validate: !showInfo && f.fieldValidate?.validate,
                        }}
                    >
                        <>
                            {getErrorType(f.accesKey) === "pattern" && (
                                <ErrorText msg={FORM_MSG.INVALID_NUMBER} />
                            )}
                            {getErrorType(f.accesKey) === "required" && (
                                <ErrorText msg={FORM_MSG.REQUIRED} />
                            )}
                            {
                                getErrorType(f.accesKey) === "validate" && (
                                    <ErrorText
                                        msg={f.fieldValidate.message}
                                    />
                                )}
                        </>
                    </InputRFH>
                </div>
            );
        case "Money":
            if (f.accesKey === "price") {
                return (
                    <>
                        <NumberFormatRFH
                            key={f.title}
                            editing={isEditable}
                            title={f.title}
                            name={f.accesKey}
                            inputClassName="!mb-0"
                            className={`flex-col ${width}`}
                            prefix={"$"}
                            inputRegister={{ ...register }}
                            suffix={""}
                            validation={{ required: true }}
                        >
                            <>
                                {priceError && <ErrorText msg={priceError} />}
                                {validating && options.num_payments === 0 && options.apply_discount && (
                                    <p className="text-xs text-color9">
                                        Paid in full 10% discount
                                    </p>
                                )}
                                {getErrorType(f.accesKey) === "required" && (
                                    <ErrorText msg={FORM_MSG.REQUIRED} />
                                )}
                            </>
                        </NumberFormatRFH>
                        {options.num_payments === 0 && options.apply_discount && !showInfo && !validating && (
                            <>
                                <NumberFormatInput
                                    key={"Paid in full cost"}
                                    editing={false}
                                    title={"Paid in full cost"}
                                    name={f.accesKey}
                                    inputClassName="!mb-0 !bg-color9"
                                    className={`flex-col ${width}`}
                                    prefix={"$"}
                                    suffix={""}
                                    value={options?.cost ? options?.cost * 0.9 : options.cost}
                                >
                                    <>{priceError && <ErrorText msg={priceError} />}</>
                                </NumberFormatInput>
                                <NumberFormatInput
                                    key={"Paid in full price"}
                                    editing={false}
                                    title={"Paid in full price"}
                                    name={f.accesKey}
                                    inputClassName="!mb-0 !bg-color9"
                                    className={`flex-col ${width}`}
                                    prefix={"$"}
                                    suffix={""}
                                    value={options?.price ? options?.price * 0.9 : options.price}
                                >
                                    <>{priceError && <ErrorText msg={priceError} />}</>
                                </NumberFormatInput>
                            </>
                        )}
                    </>

                );

            }
            if (f.accesKey === "cost") {
                return (
                    <div className={`flex flex-col w-full ${width}`}>
                        <NumberFormatRFH
                            key={f.title}
                            editing={isEditable}
                            title={f.title}
                            name={f.accesKey}
                            inputClassName="!mb-0"
                            className={`flex-col ${width}`}
                            prefix={"$"}
                            inputRegister={{ ...register }}
                            suffix={""}
                            validation={{ required: false }}
                        >
                            {validating && options.num_payments === 0 && options.apply_discount && (
                                <p className="text-xs text-color9">
                                    Paid in full 10% discount
                                </p>
                            )}
                        </NumberFormatRFH>
                    </div>
                );
            }
            return (
                <NumberFormatRFH
                    key={f.title}
                    editing={isEditable}
                    title={f.title}
                    name={f.accesKey}
                    inputClassName="!mb-0"
                    className={`flex-col ${width}`}
                    control={control}
                    prefix={"$"}
                    suffix={""}
                    inputRegister={{ ...register }}
                    validation={{
                        required: f.required,
                        validate: fieldValidate?.validate,
                    }}
                >
                    <>
                        {getErrorType(f.accesKey) === "required" && (
                            <ErrorText msg={FORM_MSG.REQUIRED} />
                        )}
                        {getErrorType(f.accesKey) === "validate" && (
                            <ErrorText
                                msg={(errors as any)[f.accesKey].message}
                            />
                        )}
                        {fieldValidate &&
                            getErrorType(f.accesKey) === "validate" && (
                                <ErrorText msg={fieldValidate.message} />
                            )}
                    </>
                </NumberFormatRFH>
            );
        case "Checkbox":
            return (
                <div
                    key={f.title}
                    className={`${width} h-full flex sm:flex-col items-center justify-center pt-5`}
                >
                    <CheckboxRFH
                        editing={isEditable}
                        className="flex-col w-full"
                        titleClassname=""
                        title={f.title}
                        name={f.accesKey}
                        inputRegister={register}
                    />
                    {getErrorType(f.accesKey) === "required" && (
                        <ErrorText msg={FORM_MSG.REQUIRED} />
                    )}
                </div>
            );
        case "Dropdown":
            let dropdownOptions = (f as any).options;
            if (f.getOptions) {
                dropdownOptions = f.getOptions({ ...options });
            }
            return (
                <div
                    key={f.title}
                    className={`${width} w-full flex sm:flex-col items-center sm:items-start`}
                >
                    <DropDownGeneric
                        title={f.title}
                        inputClassName="w-full"
                        editing={isEditable}
                        readonly={!isEditable}
                        className="w-full"
                        value={(options as any)[f.accesKey]}
                        name="legal_status"
                        onChangeFunction={(val) =>
                            setValue(f.accesKey as any, val, {
                                shouldValidate: true,
                            })
                        }
                        selectOptions={dropdownOptions}
                        isObject={f.isObject}
                        objectKeyValue={f.objectKeyValue}
                        objectKeyDisplay={f.objectKeyDisplay}
                        menuPosition="fixed"
                    />
                    {getErrorType(f.accesKey) === "required" && (
                        <ErrorText msg={FORM_MSG.REQUIRED} />
                    )}
                    {getErrorType(f.accesKey) === "validate" && (
                        <ErrorText msg={f.validationMessage} />
                    )}
                </div>
            );
        case "Input":
            return (
                <div
                    key={f.title}
                    className={`${width} flex sm:flex-col items-center sm:items-start`}
                >
                    <span className="">{f.title}</span>
                    <InputRFH
                        editing={isEditable}
                        className="flex-col w-full"
                        containerClassName="!mb-0"
                        name={f.accesKey}
                        inputRegister={register}
                        validation={{
                            ...formHandleEmpty,
                            required: f.required,
                        }}
                    />
                    {getErrorType(f.accesKey) === "required" && (
                        <ErrorText msg={FORM_MSG.INVALID_NUMBER} />
                    )}
                </div>
            );
        case "Bureaus":
            const changeBureau = (key: "ex" | "eq" | "tu", value: boolean) => {
                const values = {
                    ex: options.ex,
                    tu: options.tu,
                    eq: options.eq,
                };
                values[key] = value;
                setValue(key, value);
            };

            return (
                <div
                    key={f.title}
                    className={`${width} flex sm:flex-col items-center sm:items-start`}
                >
                    <span className="mb-2">{f.title}</span>
                    <div className="flex flex-row gap-x-4 items-center h-full">
                        <Input
                            editing={isEditable}
                            type="checkbox"
                            checkboxNotClickable={false}
                            title="EQ"
                            className={` md:col-span-5 flex-row-reverse gap-2 justify-start my-4 md:my-0 md:justify-center lg:justify-start md:items-center`}
                            inputClassName={`p-3 custom_checkbox`}
                            value={"certified"}
                            titleClassname=""
                            isChecked={Boolean(options.eq)}
                            onChangeFunction={() =>
                                changeBureau("eq", !options.eq)
                            }
                        />
                        <Input
                            editing={isEditable}
                            type="checkbox"
                            checkboxNotClickable={false}
                            title="TU"
                            className={` md:col-span-5 flex-row-reverse gap-2 justify-start my-4 md:my-0 md:justify-center lg:justify-start md:items-center`}
                            inputClassName={`p-3 custom_checkbox`}
                            value={"certified"}
                            titleClassname=""
                            isChecked={Boolean(options.tu)}
                            onChangeFunction={() =>
                                changeBureau("tu", !options.tu)
                            }
                        />
                        <Input
                            editing={isEditable}
                            type="checkbox"
                            checkboxNotClickable={false}
                            title="EX"
                            className={`md:col-span-5 flex-row-reverse gap-2 justify-start my-4 md:my-0 md:justify-center lg:justify-start md:items-center`}
                            inputClassName={`p-3 custom_checkbox`}
                            value={"certified"}
                            titleClassname=""
                            isChecked={Boolean(options.ex)}
                            onChangeFunction={() =>
                                changeBureau("ex", !options.ex)
                            }
                        />
                    </div>
                    {getErrorType("ex") === "validate" && (
                        <ErrorText msg={errors.ex?.message || ""} />
                    )}
                </div>
            );
        case "AccountNumbers":
            return (
                <>
                    <AccountNumbers
                        title={f.title}
                        isEditable={isEditable}
                        width={width}
                        control={control}
                    />
                    {get(errors, "account_numbers")?.root?.type ===
                        "required" && (
                            <div className="w-full col-span-8">
                                <ErrorText msg={FORM_MSG.REQUIRED} />
                            </div>
                        )}
                </>
            );
        case "CreditReport":
            // if (validating && addProgram) return "";

            const changeCreditReport = (key: "credit_report", value: string) => {
                if (!value)
                    setError(key, { type: "required", message: "* Please select at least one bureau." });
                else clearErrors(key);
                setValue(key, value);
            };
            return (
                <>
                    <SelectCreditReport
                        key={f.title}
                        id={person_id}
                        validating={validating && !isEditingProgram}
                        value={options.credit_report}
                        setValue={changeCreditReport}
                        lead={lead}
                    />
                    {getErrorType("credit_report") === "required" && (
                        <div className="w-full col-span-8">
                            <ErrorText msg={FORM_MSG.REQUIRED} />
                        </div>
                    )}
                    <div className="w-full flex flex-col col-span-8">
                        <p className="font-bold text-lg">
                            Credit Report Notes
                        </p>
                        <textarea
                            className={`outline-none w-full text-color7 border-color3 text-r-medium-small-2 focus:ring-color1
                                    focus:border-none transition-all rounded-[7px] py-1 px-2 mb-0 h-20 ${validating && !isEditingProgram && "bg-color15 text-color1"
                                }`}
                            {...register("notes_credit", {})}
                            disabled={validating && !isEditingProgram}
                        />
                    </div>
                </>
            );
        case "Collections":
            // if (validating && addProgram) return "";
            const collections = ["1", "2", "3"];
            return (
                <div className="flex flex-col col-span-8 -mb-4">
                    <div className="lg:col-span-8 font-bold mb-0">
                        Collections
                    </div>
                    {collections.map((c: any) => (
                        <div
                            key={f.title + c}
                            className="grid grid-cols-8 gap-2"
                        >
                            <div
                                className={`lg:col-span-2 col-span-3 w-full flex sm:flex-col items-center sm:items-start`}
                            >
                                <DropDownCreditors
                                    title={"Creditor"}
                                    inputClassName="w-full mt-2"
                                    editing={isEditable}
                                    className="w-full"
                                    value={
                                        (options as any)[`creditor_${c}`]
                                    }
                                    name={`creditor_${c}`}
                                    onChangeFunction={(val) =>
                                        setValue(
                                            `creditor_${c}` as any,
                                            val
                                        )
                                    }
                                />
                                {getErrorType(`creditor_${c}`) ===
                                    "required" && (
                                        <ErrorText msg={FORM_MSG.REQUIRED} />
                                    )}
                            </div>
                            <div
                                className={`lg:col-span-3 col-span-3 flex flex-col items-start`}
                            >
                                <span className="mb-2">{"Account"}</span>
                                <InputRFH
                                    editing={isEditable}
                                    className="flex-col w-full"
                                    titleClassname=""
                                    name={`account_${c}` as any}
                                    inputRegister={register}
                                    validation={{
                                        ...formHandleEmpty,
                                    }}
                                />
                                {getErrorType(`account_${c}`) ===
                                    "required" && (
                                        <ErrorText msg={FORM_MSG.REQUIRED} />
                                    )}
                            </div>
                            <div
                                className={`lg:col-span-3 col-span-2 w-full flex sm:flex-col items-center sm:items-start`}
                            >
                                <NumberFormatRFH
                                    editing={isEditable}
                                    title={"Collection Amount"}
                                    name={`collection_amount_${c}`}
                                    inputClassName="w-full mt-2 mb-0"
                                    className={`flex-col mb-4`}
                                    control={control}
                                    prefix={"$"}
                                    suffix={""}
                                    validation={{
                                        validate: (val) => !val || val > 0,
                                    }}
                                >
                                    <>
                                        {getErrorType(
                                            `collection_amount_${c}`
                                        ) === "required" && (
                                                <ErrorText
                                                    msg={FORM_MSG.REQUIRED}
                                                />
                                            )}
                                        {getErrorType(
                                            `collection_amount_${c}`
                                        ) === "validate" && (
                                                <ErrorText
                                                    msg={FORM_MSG.MIN(0)}
                                                />
                                            )}
                                    </>
                                </NumberFormatRFH>
                            </div>
                        </div>
                    ))}
                </div>
            );
        case "Payments":
            return (
                <div className="flex flex-col col-span-8">
                    <div className="lg:col-span-8 font-bold mb-0">
                        Payment
                    </div>
                    <PaymentsDynamicFields
                        editing={(!showInfo || !!isEditingProgram) && options.status !== "CANCELED"}
                        setValue={setValue}
                        getErrorType={getErrorType}
                        watch={watch}
                        control={control}
                        register={register}
                        errors={errors}
                        clientId={person_id}
                        person_id={person_id}
                        isLead={lead}
                    />
                </div>
            );
    }
    return <></>;
}